import React, { useState } from 'react';
import {
  ShieldAlert,
  AlertTriangle,
  X,
  CheckCircle2,
  Lock,
  Upload,
  Send,
  RefreshCw,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { submitSafetyReport } from '../../lib/supabaseService';
import type { SafetyReportCategory } from '../../types';

interface ReportProfessionalModalProps {
  professionalId: string;
  professionalName?: string;
  jobId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ReportProfessionalModal: React.FC<ReportProfessionalModalProps> = ({
  professionalId,
  professionalName = 'Professional',
  jobId,
  onClose,
  onSuccess,
}) => {
  const { currentUser } = useApp();

  const [category, setCategory] = useState<SafetyReportCategory>('identity_concern');
  const [description, setDescription] = useState<string>('');
  const [evidenceFileName, setEvidenceFileName] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submittedReportId, setSubmittedReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || description.trim().length < 10) {
      setError('Please provide a descriptive explanation of the safety incident (at least 10 characters).');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await submitSafetyReport({
        professionalId,
        reportedById: currentUser?.id || 'anonymous_customer',
        jobId,
        reasonCategory: category,
        description: description.trim(),
        evidenceUrls: evidenceFileName ? [evidenceFileName] : [],
      });

      if (res?.success) {
        setSubmittedReportId(res.reportId || 'SAFETY-REPORT-CONFIRMED');
        if (onSuccess) onSuccess();
      } else {
        setError(res?.error || 'Failed to submit report. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs animate-fadeIn">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 border border-slate-200 shadow-2xl relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>

        {submittedReportId ? (
          <div className="text-center py-6 space-y-4 animate-scaleUp">
            <div className="w-16 h-16 rounded-3xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto ring-4 ring-emerald-50">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-black text-slate-900">Safety Incident Registered</h3>
              <p className="text-xs text-slate-600 max-w-sm mx-auto">
                Thank you for helping keep JobMatch NG safe. Our Trust & Safety response unit investigates all reports within 1-2 hours.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono text-slate-700">
              Reference ID: {submittedReportId}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all cursor-pointer"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center shrink-0">
                <ShieldAlert className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="text-base font-black text-slate-900">
                  Trust & Safety Report
                </h3>
                <p className="text-xs text-slate-500">
                  Reporting artisan: <strong>{professionalName}</strong>
                </p>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-rose-50 text-rose-900 border border-rose-200 text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">
                Incident Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as SafetyReportCategory)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium focus:ring-2 focus:ring-rose-500 focus:outline-none"
              >
                <option value="identity_concern">Identity Concern / Fake Name / Forged Credentials</option>
                <option value="impersonation">Impersonation (A different person arrived than profile)</option>
                <option value="suspicious_behaviour">Suspicious Behavior / Theft Risk</option>
                <option value="payment_issue">Payment Extortion / Unauthorized Off-Platform Demands</option>
                <option value="harassment">Harassment, Abuse, or Threats</option>
                <option value="unsafe_conduct">Unsafe Work / Severe Property Damage</option>
                <option value="misconduct">Professional Misconduct / Abandonment</option>
                <option value="other">Other Safety Concern</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">
                Describe What Happened
              </label>
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please describe the incident in detail, including dates, specific actions, and safety concerns..."
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium focus:ring-2 focus:ring-rose-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">
                Supporting Evidence / Photos / Audio (Optional)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*,video/*,.pdf"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      setEvidenceFileName(e.target.files[0].name);
                    }
                  }}
                  className="w-full text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-800"
                />
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-2.5 text-[11px] text-slate-500">
              <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
              <span>
                Your report is confidential. The artisan will not be given your personal identity during preliminary investigation.
              </span>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all shadow-md flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Submitting Report...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>Submit Safety Report</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
